import type { SettingsSnapshot } from '@/data-sources/contract';

const modules = [
  ['overview', 'Overview'],
  ['deepdive-ai', 'DeepDive AI'],
  ['quick-search', 'Quick search'],
  ['mentions', 'Mentions'],
  ['influencers', 'Influencers'],
  ['media', 'Media'],
  ['brand-health', 'Brand health'],
  ['community', 'Community'],
  ['competitors', 'Competitors'],
  ['trends', 'Trends'],
] as const;
const allModules = modules.map(([value]) => value);
const usage = (count: number, prefix: string, base: number) =>
  Array.from({ length: count }, (_, index) => ({
    label: `${prefix}${index + 1}`,
    credits: base + ((index * 17 + count * 3) % 38),
  }));

export const SETTINGS_SEED: SettingsSnapshot = {
  account: { name: 'Grameenphone', email: 'grameen@deepdive.ai', timezone: 'utc' },
  configuration: {
    keywords: ['#Grameenphone', '#GP', '#network', '#5g', '#offer', 'data pack'],
    topics: [
      'Network coverage',
      'Data packs',
      'Customer service',
      '5G',
      'Recharge',
      'App experience',
    ],
    languages: ['bn', 'en', 'hi', 'ur', 'ar'],
    sentimentThreshold: [25, 75],
  },
  timezones: [
    { value: 'utc', label: 'Coordinated Universal Time (UTC)', supportingText: 'UTC±00:00' },
    { value: 'dhaka', label: 'Asia/Dhaka', supportingText: 'UTC+06:00' },
    { value: 'kolkata', label: 'Asia/Kolkata', supportingText: 'UTC+05:30' },
    { value: 'dubai', label: 'Asia/Dubai', supportingText: 'UTC+04:00' },
    { value: 'london', label: 'Europe/London', supportingText: 'UTC+01:00' },
    { value: 'singapore', label: 'Asia/Singapore', supportingText: 'UTC+08:00' },
    { value: 'new-york', label: 'America/New_York', supportingText: 'UTC−04:00' },
  ],
  languageOptions: [
    { value: 'bn', label: 'Bangla' },
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'Hindi' },
    { value: 'ur', label: 'Urdu' },
    { value: 'ar', label: 'Arabic' },
    { value: 'ne', label: 'Nepali' },
  ],
  tuningLog: [
    {
      id: 'log-1',
      summary: 'Finally got 5G at Banani — took three months but worth the wait',
      previous: 'positive',
      current: 'negative',
    },
    {
      id: 'log-2',
      summary: 'Great, another “network upgrade” that knocked out my data all morning',
      previous: 'positive',
      current: 'negative',
    },
    {
      id: 'log-3',
      summary: 'Recharged through the app, took about a minute',
      previous: 'neutral',
      current: 'positive',
    },
    {
      id: 'log-4',
      summary: 'Love how my “unlimited” pack stops working at 20GB',
      previous: 'positive',
      current: 'negative',
    },
    {
      id: 'log-5',
      summary: 'Call dropped twice on the Dhaka–Chattogram highway',
      previous: 'negative',
      current: 'neutral',
    },
  ],
  teamMembers: [
    {
      id: 'john-doe',
      name: 'John Doe',
      email: 'john@deepdive.ai',
      role: 'super-admin',
      status: 'active',
      self: true,
    },
    {
      id: 'jimmy',
      name: 'Jimmy',
      email: 'jimmy@deepdive.ai',
      role: 'admin',
      status: 'pending',
      modules: [...allModules],
    },
    {
      id: 'doe',
      name: 'Doe',
      email: 'doe@deepdive.ai',
      role: 'user',
      status: 'pending',
      modules: ['overview', 'mentions', 'influencers', 'media', 'brand-health', 'trends'],
    },
    {
      id: 'sarah',
      name: 'Sarah',
      email: 'sarah@deepdive.ai',
      role: 'admin',
      status: 'active',
      modules: [...allModules],
    },
    {
      id: 'bob',
      name: 'Bob',
      email: 'bob@deepdive.ai',
      role: 'admin',
      status: 'active',
      modules: allModules.filter((id) => id !== 'deepdive-ai'),
    },
  ],
  roles: [
    {
      value: 'super-admin',
      label: 'Super admin',
      supportingText: 'Every module, plus billing and team',
    },
    { value: 'admin', label: 'Admin', supportingText: 'Manages the modules you choose' },
    { value: 'user', label: 'User', supportingText: 'Views the modules you choose' },
  ],
  moduleOptions: modules.map(([value, label]) => ({ value, label })),
  usageLog: [
    {
      id: 'u1',
      at: '2026-09-15T10:42:00+06:00',
      query: 'Summarise the latest 5G complaints',
      credits: 18,
      memberId: 'john-doe',
    },
    {
      id: 'u2',
      at: '2026-09-14T16:18:00+06:00',
      query: 'Compare brand sentiment this week',
      credits: 24,
      memberId: 'sarah',
    },
    {
      id: 'u3',
      at: '2026-09-14T11:25:00+06:00',
      query: 'What caused the network spike?',
      credits: 12,
      memberId: 'bob',
    },
    {
      id: 'u4',
      at: '2026-09-13T15:07:00+06:00',
      query: 'Find emerging customer-service themes',
      credits: 31,
      memberId: 'john-doe',
    },
    {
      id: 'u5',
      at: '2026-09-12T09:36:00+06:00',
      query: 'Draft an executive insight brief',
      credits: 27,
      memberId: 'sarah',
    },
  ],
  usage: { '1d': usage(24, '', 4), '7d': usage(7, 'Sep ', 32), '30d': usage(30, 'Aug ', 18) },
};

const clone = (): SettingsSnapshot => structuredClone(SETTINGS_SEED);
export const settingsDb = { snapshot: clone(), inviteSequence: 0 };
export function resetSettingsDb(): void {
  settingsDb.snapshot = clone();
  settingsDb.inviteSequence = 0;
}
